import {
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';

import { isTerminalPhase, type StreamState, type TraceEntry } from '@harness/stream-core';

import { now } from '../lib/environment.ts';

/**
 * The signature element. A live trace of recent wire activity, drawn as an
 * activity monitor: bars rise from a baseline, height encodes payload size,
 * colour encodes kind, and the newest arrivals burn brighter than the ones
 * ageing off the left edge.
 *
 * Three things separate this from the strip of ticks it replaces.
 *
 * The x axis is time, not arrival order, but it is quantised into a fixed
 * grid of SLOT_W-wide columns rather than one mark per timestamp. Every
 * column is a screen position, not a data index: whatever lands in a column
 * is aggregated into it (worst tone wins, tallest height wins), so a burst
 * reads as a run of full columns and a lull reads as a run of near-empty
 * ones, and two bars can never overlap. The old tape packed one tick per
 * frame at a fixed 3px stride, which drew a stall and a flood as the same
 * picture; the tape before that gave every mark its own timestamp pixel,
 * which piled a burst into an unreadable smear and scattered a lull into
 * isolated hairlines. This is the difference between a chart and a
 * decoration.
 *
 * The sweep auto-ranges to the run rather than sitting at a fixed sixty
 * seconds, so a short scenario fills the tape instead of huddling in the last
 * twelfth of it. See TIMEBASE_STEP_S below.
 *
 * And the window scrolls. While the stream is live the right edge tracks the
 * clock, so the trace slides left on its own and an idle wire visibly ages
 * instead of freezing mid frame. When the stream reaches a terminal phase the
 * edge pins to the last entry, because a finished trace is for reading, not
 * for watching drain away.
 *
 * Under prefers-reduced-motion the base stylesheet collapses every transition
 * here, so the trace steps rather than glides and the live head stops pulsing.
 * It still updates; nothing about what it reports depends on the motion.
 */

/**
 * The timebase detents, in seconds. Short runs select a tight timebase (0.5s, 1s, 2s...)
 * so scenarios fill the tape rather than being crammed into an 8-second window.
 */
const TIMEBASE_DETENTS_S = [0.5, 1, 2, 4, 8, 12, 16, 20, 30, 45, 60] as const;
/** Headroom kept ahead of the trace so the newest mark is not pressed on the edge. */
const TIMEBASE_SLACK = 1.08;

/**
 * Column geometry, the single source of truth for the width of a bar. Every
 * other place that used to hardcode this number (the CSS bar width, the
 * legend swatch, the density budget) now reads it from here, either
 * directly or via the `--tape-bar`/`--tape-slot` custom properties the
 * component sets on its root element.
 */
export const BAR_W = 6;
export const SLOT_W = 9;
const GAP = SLOT_W - BAR_W;

/** How often the live window advances. Marks settle between steps in CSS. */
const CLOCK_MS = 400;

/** Gridline positions, as a fraction of the window before the right edge. */
const AXIS = [1, 0.75, 0.5, 0.25, 0] as const;

type FrameTick = Extract<TraceEntry, { kind: 'frame' }>;

/** Marks the trace draws, in the vocabulary the legend explains. */
type Tone = 'signal' | 'hold' | 'fault' | 'faint';

/** Severity order for picking the tone, and the representative entry, of a column that holds more than one arrival. */
const TONE_RANK: Record<Tone, number> = { faint: 0, signal: 1, hold: 2, fault: 3 };

function frameTone(type: FrameTick['type']): Tone {
  if (type === 'response.error') return 'fault';
  if (
    type === 'response.queued' ||
    type === 'response.status' ||
    type === 'response.generation_status' ||
    type === 'response.truncated'
  ) {
    return 'hold';
  }
  return 'signal';
}

/**
 * Tone for every kind of trace entry, not just frames. This mirrors the
 * grouping `entryTone()` in inspector.tsx already established: fault for
 * anything that lost or broke data, hold for anything that waited or
 * retried, faint for anything correctly discarded. So the tape and the
 * frame list never disagree about what a kind means. Kept as an exhaustive
 * switch rather than a default branch: a new TraceEntry kind fails the build
 * here instead of silently drawing as an invisible mark, which is exactly
 * the bug this replaces.
 */
function toneOf(entry: TraceEntry): Tone {
  switch (entry.kind) {
    case 'frame':
      return frameTone(entry.type);
    case 'gap':
    case 'malformed':
      return 'fault';
    case 'reconnect':
    case 'watchdog':
    case 'retry':
      return 'hold';
    case 'duplicate':
    case 'stale':
    case 'post_terminal':
      return 'faint';
    case 'keepalive':
      // Not "discarded" like duplicate/stale, but drawn faint on purpose: a
      // frame with no payload is the smallest thing on the wire, and the
      // hairline is what keeps a keepalive-only stall from reading as dead.
      return 'faint';
    case 'sentinel':
    case 'unknown_event':
    case 'replace':
    case 'cancelled':
      return 'signal';
  }
}

/** Payload size, in the same units heightOf scales by, or 0 for entries with none. */
function entryBytes(entry: TraceEntry): number {
  if (entry.kind === 'frame') return entry.bytes;
  if (entry.kind === 'replace') return entry.charactersRemoved;
  return 0;
}

/**
 * Format an axis time step based on the total span being displayed.
 */
function formatAxisTime(seconds: number): string {
  if (seconds <= 0) return '0s';
  if (seconds < 1) {
    const s = seconds.toFixed(2);
    return `${s.endsWith('.00') ? s.slice(0, -3) : s.endsWith('0') ? s.slice(0, -1) : s}s`;
  }
  const s = seconds.toFixed(1);
  return `${s.endsWith('.0') ? s.slice(0, -2) : s}s`;
}

/**
 * Custom properties are how a mark passes its geometry and its brightness to
 * the stylesheet, which keeps every colour and every shadow in CSS where the
 * tokens live. React types style values as strings, so the cast belongs here
 * once rather than at each of the four call sites.
 */
function vars(values: Record<string, string>): CSSProperties {
  return values as CSSProperties;
}

/**
 * One screen column of the grid. Always present for every slot the plot can
 * hold, even when nothing landed in it (an empty column renders at
 * `height: 0`), which keeps React's reconciliation on a stable per-position
 * node instead of a stable per-entry one, so a column can animate its own
 * height without ever being handed another column's transition.
 */
interface Column {
  height: number;
  tone: Tone;
  count: number;
  /** The most severe member, breaking ties on largest payload; null for an empty column. */
  entry: TraceEntry | null;
  /** Timestamp of the newest member, for the glow falloff; null for an empty column. */
  newestAt: number | null;
}

interface Break {
  key: string;
  at: number;
  beforeId: number | null;
  afterId: number | null;
}

/**
 * A frame that carried no payload still proves the wire was open. Drawing it
 * as a hairline is what keeps the keepalive-only stall honest: without it the
 * scenario whose whole point is "the connection is alive and sending nothing"
 * draws an empty tape, which is the one reading that is definitely wrong.
 */
const KEEPALIVE_HEIGHT = 0.08;

/** Height the median sized frame in the window is drawn at. */
const MEDIAN_HEIGHT = 0.45;
/** Log span, two factors of ten, that fills the rest of the band. */
const DECADES = Math.log(10) * 2;
/** How far one decade (10x the median) sits above MEDIAN_HEIGHT, in the same
    fraction heightOf() scales by. Drawn as the tape's second horizontal
    gridline, so a bar's height reads as a magnitude against a labelled
    scale rather than only as a shape. */
const DECADE_OFFSET = Math.log(10) / DECADES;

/**
 * Payload size to a fraction of the plot height, on a log scale anchored to
 * the window's median rather than to its extremes.
 *
 * Anchoring to the extremes was tried twice and failed in both directions. The
 * maximum pressed a hundred and fifty text deltas into identical stubs to make
 * room for one usage frame; a high percentile landed inside the delta cluster
 * and clamped all of them at full height, drawing a flat topped block. Both
 * also move the whole trace whenever a single outlier enters or leaves the
 * window, which makes the tape twitch for reasons that have nothing to do with
 * the data.
 *
 * The median does not move like that. Ordinary traffic gets the middle of the
 * band to vary in, a frame ten times the median reaches near full scale, and
 * the scale means the same thing from one second to the next.
 *
 * The floor sits above KEEPALIVE_HEIGHT on purpose: a payload frame, however
 * tiny, is never pixel-identical to a keepalive, which carries none at all.
 */
function heightOf(bytes: number, median: number): number {
  const offset = (Math.log(bytes + 1) - median) / DECADES;
  return Math.min(1, Math.max(0.12, MEDIAN_HEIGHT + offset));
}

/**
 * Height for any kind of trace entry, not just frames. `median` is null when
 * the window holds no frame entries at all: there is nothing to anchor a
 * log scale to, so entries fall back to MEDIAN_HEIGHT directly rather than
 * running heightOf against a fabricated zero median, which used to draw a
 * solid full-height block for a window with, say, only a reconnect in it.
 */
function heightFor(entry: TraceEntry, median: number | null): number {
  switch (entry.kind) {
    case 'frame':
      return median === null ? MEDIAN_HEIGHT : heightOf(entry.bytes, median);
    case 'gap':
    case 'malformed':
      // Forced to full height on purpose: lost or unparseable data must be
      // unmissable, never folded flat by whatever else shares its column.
      return 1;
    case 'keepalive':
      return KEEPALIVE_HEIGHT;
    case 'replace':
      return median === null ? MEDIAN_HEIGHT : heightOf(entry.charactersRemoved, median);
    case 'reconnect':
      // Never bucketed into a column; drawn as a break line instead.
      return 0;
    case 'sentinel':
    case 'duplicate':
    case 'stale':
    case 'unknown_event':
    case 'post_terminal':
    case 'watchdog':
    case 'cancelled':
    case 'retry':
      return MEDIAN_HEIGHT;
  }
}

function columnCount(width: number): number {
  return Math.max(1, Math.floor((width + GAP) / SLOT_W));
}

/** Right-aligns the grid so the last column's bar is flush with "now". */
function originXOf(width: number, columns: number): number {
  return width - (columns * SLOT_W - GAP);
}

function columnOf(at: number, windowStart: number, windowMs: number, columns: number): number {
  const raw = Math.floor(((at - windowStart) / windowMs) * columns);
  return Math.min(columns - 1, Math.max(0, raw));
}

export function WireTape({ state }: { state: StreamState }): ReactElement {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const plotId = useId();
  /*
   * The pointer's own offset is the state, not the mark it landed on. Holding
   * the mark instead looks identical until the trace is live, and then the
   * window scrolls out from under it: the readout stays pinned to its frame
   * and walks away from the cursor that selected it. Storing the offset and
   * re-resolving the nearest column on every render keeps the readout under
   * the pointer, and the selection updates as the trace slides beneath it.
   */
  const [pointerX, setPointerX] = useState<number | null>(null);

  /*
   * The trace is measured in real pixels from the element rather than a fixed
   * coordinate space stretched to fit, which is what makes the column grid
   * resolve to the same picture at every width: the number of columns
   * changes with the measured width, but each one is always exactly BAR_W
   * wide with GAP of air beside it.
   */
  useEffect(() => {
    const element = trackRef.current;
    if (element === null) return;
    const observer = new ResizeObserver((entries) => {
      const rect = entries[0]?.contentRect;
      if (rect === undefined) return;
      setWidth((current) => (current === rect.width ? current : rect.width));
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, []);

  const live = state.phase !== 'idle' && !isTerminalPhase(state.phase);
  const [clock, setClock] = useState(() => now());

  /*
   * The window only chases the clock while there is something to chase. A
   * terminal stream pins its right edge to the last entry so the evidence
   * stays on screen, and an idle client runs no timer at all.
   */
  useEffect(() => {
    if (!live) return;
    setClock(now());
    const id = window.setInterval(() => {
      setClock(now());
    }, CLOCK_MS);
    return () => {
      window.clearInterval(id);
    };
  }, [live]);

  const hasEntries = state.timeline.length > 0;
  const lastAt = state.timeline.at(-1)?.at ?? 0;
  const rightEdge = live ? Math.max(clock, lastAt) : lastAt;
  const firstAt = state.timeline[0]?.at ?? rightEdge;

  /*
   * The sweep auto-ranges to the shortest detent that holds the run,
   * starting from 0.5s up to 60s. This ensures short sub-second scenarios
   * fill the tape rather than being crammed into an 8-second window.
   */
  const spanMs = (rightEdge - firstAt) * TIMEBASE_SLACK;
  const spanSeconds = spanMs / 1000;
  const windowSeconds = !hasEntries ? 8 : (TIMEBASE_DETENTS_S.find((d) => d >= spanSeconds) ?? 60);
  const windowMs = windowSeconds * 1000;
  const windowStart = rightEdge - windowMs;

  /* Roughly the newest eighth of the sweep burns, so the live edge reads consistently. */
  const glowMs = Math.min(4_000, Math.max(300, windowMs * 0.12));

  const visible = useMemo(
    () => state.timeline.filter((entry) => entry.at >= windowStart),
    [state.timeline, windowStart],
  );

  const columns = columnCount(width);
  const originX = originXOf(width, columns);
  const xOfColumn = (col: number): number => originX + col * SLOT_W;
  /* A true point-in-time coordinate, not a bar-left-edge one: reconnect
     breaks are rules, not bars, so they are never bucketed into a column
     and keep this unquantised mapping. */
  const xOf = (at: number): number => ((at - windowStart) / windowMs) * width;

  const cols = useMemo<Column[]>(() => {
    /*
     * A logarithmic scale across the window's own range, which is the only
     * one that draws this data with a shape. Payload sizes are log
     * distributed: a hundred and fifty text deltas clustered between 20 and 80
     * bytes, and a handful of tool results and usage frames two orders of
     * magnitude above them. Scaling linearly against the maximum presses the
     * whole cluster into an identical stub on the baseline. Scaling against a
     * high percentile does the opposite and clamps the whole cluster at full
     * height, which is a flat topped block wearing a chart's clothes. Mapping
     * the log of the range across the height gives the cluster the middle of
     * the band to vary in and leaves the top for the frames that earn it.
     */
    const frames = visible.filter((entry): entry is FrameTick => entry.kind === 'frame');
    const median =
      frames.length === 0
        ? null
        : (frames
            .map((entry) => Math.log(entry.bytes + 1))
            .sort((a, b) => a - b)
            .at(Math.floor(frames.length / 2)) ?? 0);

    const out: Column[] = Array.from({ length: columns }, () => ({
      height: 0,
      tone: 'faint',
      count: 0,
      entry: null,
      newestAt: null,
    }));

    for (const entry of visible) {
      // Reconnects are rules between columns, not bars: they never absorb
      // into one, and never displace what is actually in it.
      if (entry.kind === 'reconnect') continue;

      const bucket = out[columnOf(entry.at, windowStart, windowMs, columns)];
      if (bucket === undefined) continue;

      bucket.count += 1;
      if (bucket.newestAt === null || entry.at > bucket.newestAt) bucket.newestAt = entry.at;

      const height = heightFor(entry, median);
      if (height > bucket.height) bucket.height = height;

      const tone = toneOf(entry);
      if (bucket.entry === null) {
        bucket.tone = tone;
        bucket.entry = entry;
      } else {
        const rank = TONE_RANK[tone];
        const bucketRank = TONE_RANK[bucket.tone];
        if (rank > bucketRank) {
          bucket.tone = tone;
          bucket.entry = entry;
        } else if (rank === bucketRank && entryBytes(entry) > entryBytes(bucket.entry)) {
          bucket.entry = entry;
        }
      }
    }

    return out;
  }, [visible, columns, windowStart, windowMs]);

  const breaks = useMemo<Break[]>(() => {
    const out: Break[] = [];
    visible.forEach((entry, index) => {
      if (entry.kind !== 'reconnect') return;
      let before: FrameTick | null = null;
      for (let i = index - 1; i >= 0; i -= 1) {
        const candidate = visible[i];
        if (candidate !== undefined && candidate.kind === 'frame' && candidate.id !== null) {
          before = candidate;
          break;
        }
      }
      const after = visible
        .slice(index + 1)
        .find((e): e is FrameTick => e.kind === 'frame' && e.id !== null);
      out.push({
        // A reconnect's own attempt number and timestamp identify it
        // uniquely and don't shift as older entries age off the left, unlike
        // an array index.
        key: `${String(entry.attempt)}-${String(entry.at)}`,
        at: entry.at,
        beforeId: before?.id ?? null,
        afterId: after?.id ?? null,
      });
    });
    return out;
  }, [visible]);

  /*
   * The pointer selects the nearest column that actually holds something
   * rather than requiring a hit on a 6px target or landing on an empty slot
   * between arrivals. A trace you have to aim at is a trace nobody reads.
   */
  let hoveredCol: number | null = null;
  if (pointerX !== null && columns > 0) {
    const approx = Math.min(
      columns - 1,
      Math.max(0, Math.round((pointerX - originX - BAR_W / 2) / SLOT_W)),
    );
    let bestDistance = Number.POSITIVE_INFINITY;
    for (let i = 0; i < columns; i += 1) {
      const candidate = cols[i];
      if (candidate === undefined || candidate.count === 0) continue;
      const distance = Math.abs(i - approx);
      if (distance < bestDistance) {
        bestDistance = distance;
        hoveredCol = i;
      }
    }
  }
  const hoveredColumn = hoveredCol === null ? null : (cols[hoveredCol] ?? null);
  const hoveredX = hoveredCol === null ? 0 : xOfColumn(hoveredCol) + BAR_W / 2;

  /*
   * The readout chip is clamped fully inside the plot by measuring its own
   * width and keeping its centre at least half that width from either edge,
   * rather than clamping the percentage that feeds a translateX(-50%): a
   * clamp on the anchor point still lets half the chip past the edge.
   */
  const readoutRef = useRef<HTMLSpanElement | null>(null);
  const [readoutWidth, setReadoutWidth] = useState(0);
  useLayoutEffect(() => {
    setReadoutWidth(readoutRef.current?.offsetWidth ?? 0);
  });
  const readoutHalf = readoutWidth / 2;
  const readoutLeft =
    width <= readoutWidth
      ? width / 2
      : Math.min(Math.max(hoveredX, readoutHalf), width - readoutHalf);

  const frameCount = visible.filter((entry) => entry.kind === 'frame').length;

  /*
   * The section carries the landmark name and the plot carries the reading.
   * A <section> is only exposed as a region once it has an accessible name,
   * and without one every child of the tape counts as content outside any
   * landmark. Splitting the two also keeps them from announcing the same
   * sentence twice: "Wire trace" then "156 frames in the last 12 seconds".
   *
   * Neither string says "scenario". The word inside an accessible name here
   * collides with the scenario select's own label under any substring match,
   * which is how the e2e suite reaches that control.
   */
  const reading =
    frameCount === 0
      ? 'No frames yet'
      : `${String(frameCount)} frames in the last ${String(windowSeconds)} seconds, ${String(state.reconnects)} reconnects`;

  return (
    <section
      className="wire-tape"
      aria-label="Wire trace"
      data-live={live ? 'true' : 'false'}
      data-collapsed={collapsed ? 'true' : 'false'}
      style={vars({ '--tape-bar': `${String(BAR_W)}px`, '--tape-slot': `${String(SLOT_W)}px` })}
    >
      <div className="wire-tape__chrome">
        <div className="wire-tape__chrome-left">
          {/* The sweep is named, not assumed. It changes with the run, and a
              trace whose axis moves without saying so is a trace that lies. */}
          <p className="wire-tape__label">
            Wire trace{frameCount === 0 && '. Run a scenario to draw it.'}
          </p>
          <span className="mono wire-tape__sweep">{windowSeconds}s</span>
        </div>
        <ul className="wire-tape__legend">
          <li className="wire-tape__key wire-tape__key--signal">text</li>
          <li className="wire-tape__key wire-tape__key--hold">status</li>
          <li
            className="wire-tape__key wire-tape__key--fault"
            title="Error, gap, or malformed frame. Full height means data was lost."
          >
            fault
          </li>
          <li
            className="wire-tape__key wire-tape__key--reconnect"
            title="Socket dropped and resumed. A boundary line, not a bar."
          >
            reconnect
          </li>
          <li className="wire-tape__key wire-tape__key--faint">keepalive</li>
        </ul>
        <button
          type="button"
          className="button button--quiet button--icon wire-tape__collapse"
          data-collapsed={collapsed ? 'true' : 'false'}
          aria-expanded={!collapsed}
          aria-controls={plotId}
          aria-label={collapsed ? 'Expand wire trace' : 'Collapse wire trace'}
          title={collapsed ? 'Expand wire trace' : 'Collapse wire trace'}
          onClick={() => {
            setCollapsed((current) => !current);
          }}
        >
          <CollapseIcon />
        </button>
      </div>

      <div
        id={plotId}
        className="wire-tape__plot"
        role="img"
        aria-label={reading}
        onPointerMove={(event) => {
          const element = trackRef.current;
          if (element === null) return;
          setPointerX(event.clientX - element.getBoundingClientRect().left);
        }}
        onPointerLeave={() => {
          setPointerX(null);
        }}
      >
        <div className="wire-tape__grid" aria-hidden="true">
          {AXIS.map((fraction) => (
            <span
              key={fraction}
              className="wire-tape__gridline"
              style={vars({ left: `${String((1 - fraction) * 100)}%` })}
            />
          ))}
        </div>

        <div className="wire-tape__hgrid" aria-hidden="true">
          <span
            className="wire-tape__hgridline"
            style={vars({ bottom: `${(MEDIAN_HEIGHT * 100).toFixed(1)}%` })}
          />
          <span
            className="wire-tape__hgridline-label mono"
            style={vars({ bottom: `${(MEDIAN_HEIGHT * 100).toFixed(1)}%` })}
          >
            &times;1
          </span>
          <span
            className="wire-tape__hgridline"
            style={vars({
              bottom: `${(Math.min(1, MEDIAN_HEIGHT + DECADE_OFFSET) * 100).toFixed(1)}%`,
            })}
          />
          <span
            className="wire-tape__hgridline-label mono"
            style={vars({
              bottom: `${(Math.min(1, MEDIAN_HEIGHT + DECADE_OFFSET) * 100).toFixed(1)}%`,
            })}
          >
            &times;10
          </span>
        </div>

        <div className="wire-tape__track" ref={trackRef}>
          {width > 0 &&
            cols.map((column, index) => (
              // The key is the column's screen position, genuinely stable
              // across renders: as data ages off the left, a given index
              // keeps meaning "this many slots from now", not "this data
              // entry", so React never hands one column's in-flight height
              // transition to a bar that has nothing to do with it.
              <span
                key={index}
                className={`wire-tape__tick wire-tape__tick--${column.tone}`}
                style={vars({
                  left: `${xOfColumn(index).toFixed(2)}px`,
                  height: `${(column.height * 100).toFixed(2)}%`,
                  '--lit':
                    column.newestAt === null
                      ? '0'
                      : Math.max(0, 1 - (rightEdge - column.newestAt) / glowMs).toFixed(3),
                })}
              />
            ))}

          {width > 0 &&
            breaks.map((brk) => (
              <span
                key={brk.key}
                className="wire-tape__break"
                style={vars({ transform: `translateX(${xOf(brk.at).toFixed(2)}px)` })}
              >
                {/* Kept as an element with real area rather than a bare rule:
                    the e2e suite asserts on it, and a zero width box is a
                    fragile thing to assert on. */}
                <span className="wire-tape__break-line" />
                <span className="wire-tape__break-id wire-tape__break-id--before mono">
                  {brk.beforeId ?? '?'}
                </span>
                <span className="wire-tape__break-id wire-tape__break-id--after mono">
                  {brk.afterId ?? '?'}
                </span>
              </span>
            ))}

          {/* The live head. It sits at the right edge while the wire is open
              and disappears the moment the stream reaches a terminal phase,
              so the tape says whether it is still recording without a word.
              The newest column's own right edge lands exactly on the plot's
              right edge by construction (see originXOf), so the head, the
              write edge, and the "now" axis label always agree. */}
          {live && <span className="wire-tape__head" aria-hidden="true" />}

          {hoveredColumn !== null && (
            <span
              className="wire-tape__scrub"
              aria-hidden="true"
              style={vars({ transform: `translateX(${hoveredX.toFixed(2)}px)` })}
            />
          )}
        </div>

        <div className="wire-tape__baseline" aria-hidden="true" />

        {hoveredColumn !== null && (
          <span
            ref={readoutRef}
            className="wire-tape__readout mono"
            aria-hidden="true"
            style={vars({ left: `${readoutLeft.toFixed(2)}px` })}
          >
            {describeColumn(hoveredColumn)}
          </span>
        )}
      </div>

      <div className="wire-tape__axis" aria-hidden="true">
        {AXIS.map((fraction) => (
          <span
            key={fraction}
            className="wire-tape__axis-mark mono"
            data-edge={fraction === 1 ? 'start' : fraction === 0 ? 'end' : 'mid'}
            style={vars({ left: `${String((1 - fraction) * 100)}%` })}
          >
            {fraction === 0 ? 'now' : formatAxisTime(windowSeconds * fraction)}
          </span>
        ))}
      </div>
    </section>
  );
}

/** One line naming exactly what a single trace entry is. */
function describe(entry: TraceEntry): string {
  switch (entry.kind) {
    case 'frame':
      return `${entry.type} · id ${entry.id === null ? 'none' : String(entry.id)} · ${String(entry.bytes)}B`;
    case 'gap':
      return `gap · ${String(entry.fromId)} to ${String(entry.toId)}${entry.permanent ? ' · permanent' : ''}`;
    case 'malformed':
      return `malformed · ${entry.reason}`;
    case 'keepalive':
      return 'keepalive';
    case 'sentinel':
      return 'sentinel';
    case 'duplicate':
      return `duplicate · id ${String(entry.id)}`;
    case 'stale':
      return `stale · id ${String(entry.id)}`;
    case 'unknown_event':
      return `unknown event · ${entry.event}`;
    case 'post_terminal':
      return `discarded after terminal · ${entry.description}`;
    case 'replace':
      return `replace · ${String(entry.charactersRemoved)} chars removed`;
    case 'reconnect':
      return `reconnect · attempt ${String(entry.attempt)} from #${String(entry.lastEventId)}`;
    case 'watchdog':
      return `${entry.watchdog} watchdog fired`;
    case 'cancelled':
      return 'cancelled';
    case 'retry':
      return 'retry';
  }
}

/** Points up expanded, down collapsed; the rotation is a CSS rule keyed off
    the button's own data-collapsed attribute, the same pattern the
    reasoning panel's disclosure triangle already uses. */
function CollapseIcon(): ReactElement {
  return (
    <svg
      className="wire-tape__collapse-icon"
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m6 15 6-6 6 6" />
    </svg>
  );
}

/** Shorthand used inside a multi-entry column's readout: kind and size, no id. */
function summarize(entry: TraceEntry): string {
  if (entry.kind === 'frame') return `${entry.type} · ${String(entry.bytes)}B`;
  return describe(entry);
}

/** What the scrub readout prints for a column: one entry's own description, or a count plus its worst member's. */
function describeColumn(column: Column): string {
  if (column.entry === null) return '';
  if (column.count <= 1) return describe(column.entry);
  return `${String(column.count)} frames · ${summarize(column.entry)}`;
}
